/**
 * Plan entitlements — what a business may use (features) and how much (limits).
 *
 * The registry content is LOCKED by `docs/plan-limits-plan.md` (§3 features,
 * §4 limits). Change that document first, then this file. The API, the admin
 * app, the POS and (through `GET /public/plans`) the website all read these
 * lists, so a key renamed here breaks stored plan rows and the pricing page.
 *
 * A `subscription_plans` row holds the plan's values; `tenant_limits` holds a
 * business's overrides and extra features. NULL limit = unlimited.
 */

export interface PlanFeatureDefinition {
    key: string;
    /** Short name shown in the plan editor, My Subscription and the website. */
    label: string;
    /** One plain-English sentence. */
    description: string;
}

export interface PlanLimitDefinition {
    /** Key used in API payloads, e.g. `invoices_per_month`. */
    key: string;
    /** Column on `subscription_plans` / `tenant_limits`. */
    column: string;
    label: string;
    unit: 'count' | 'mb' | 'per_minute';
    period: 'month' | 'total' | 'minute';
    /** False = warn only, never refuse (bills; the rate limit keeps its own guard). */
    blocking: boolean;
}

/** Plan §3, in table order. Core features (billing, products, …) are never gated; not listed. */
export const PLAN_FEATURES = [
    {
        key: 'kds',
        label: 'Kitchen display',
        description: 'Orders show on a kitchen screen so cooks know what to make.',
    },
    {
        key: 'festive',
        label: 'Festival bookings',
        description: 'Take advance bookings and big orders for festivals and events.',
    },
    {
        key: 'voice_pos',
        label: 'Voice billing',
        description: 'Add items to a bill by speaking.',
    },
    {
        key: 'credit_billing',
        label: 'Credit billing',
        description: 'Let trusted customers buy now and pay later.',
    },
    {
        key: 'loyalty',
        label: 'Loyalty points and vouchers',
        description: 'Reward regular customers with points and discount vouchers.',
    },
    {
        key: 'memberships',
        label: 'Memberships',
        description: 'Sell membership plans with special prices.',
    },
    {
        key: 'marketing',
        label: 'Marketing messages',
        description: 'Send offers, deal of the day and re-order links, and collect feedback.',
    },
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        description: 'Send bills and messages on WhatsApp.',
    },
    {
        key: 'online_store',
        label: 'Online store',
        description: 'Your own shop website where customers can order.',
    },
    {
        key: 'online_payments',
        label: 'Online payments',
        description: 'Take UPI and card payments online.',
    },
    {
        key: 'stock_transfers',
        label: 'Stock transfers',
        description: 'Move stock between your shops.',
    },
    {
        key: 'purchase',
        label: 'Purchases',
        description: 'Manage suppliers, price requests, purchase orders and supplier bills.',
    },
    {
        key: 'quotations',
        label: 'Quotations and sales orders',
        description: 'Send price quotes and turn them into orders and invoices.',
    },
    {
        key: 'corporate_accounts',
        label: 'Corporate accounts',
        description: 'Sell to companies with their own prices and monthly bills.',
    },
    {
        key: 'commissions',
        label: 'Staff commissions',
        description: 'Pay staff a share of the sales they make.',
    },
    {
        key: 'staff_attendance',
        label: 'Attendance and shift swaps',
        description: 'Track staff attendance and let staff swap shifts.',
    },
    {
        key: 'advanced_reports',
        label: 'Advanced reports',
        description: 'Deeper reports: best sellers, busy hours, staff and customer trends.',
    },
    {
        key: 'tally_export',
        label: 'Tally export',
        description: 'Send your sales and purchases to Tally.',
    },
    {
        key: 'demand_forecast',
        label: 'Demand forecast',
        description: 'See how much stock you may need next.',
    },
    {
        key: 'einvoice',
        label: 'E-invoice',
        description: 'Create GST e-invoices with an IRN number.',
    },
    {
        key: 'batch_serial',
        label: 'Batch and serial tracking',
        description: 'Track batches, expiry dates and serial numbers.',
    },
] as const satisfies readonly PlanFeatureDefinition[];

export type PlanFeatureKey = (typeof PLAN_FEATURES)[number]['key'];

export const PLAN_FEATURE_KEYS: readonly PlanFeatureKey[] = PLAN_FEATURES.map((f) => f.key);

/** Narrows stored strings (plan rows, `extra_features`) to known keys; `basic_pos` fails. */
export function isPlanFeatureKey(value: unknown): value is PlanFeatureKey {
    return (
        typeof value === 'string' && (PLAN_FEATURE_KEYS as readonly string[]).includes(value)
    );
}

/** Plan §4, in table order. */
export const PLAN_LIMITS = [
    {
        key: 'locations',
        column: 'max_locations',
        label: 'Locations',
        unit: 'count',
        period: 'total',
        blocking: true,
    },
    {
        key: 'users',
        column: 'max_users',
        label: 'Users',
        unit: 'count',
        period: 'total',
        blocking: true,
    },
    {
        key: 'invoices_per_month',
        column: 'max_invoices_per_month',
        label: 'Bills per month',
        unit: 'count',
        period: 'month',
        blocking: false,
    },
    {
        key: 'reports_per_month',
        column: 'max_reports_per_month',
        label: 'Reports per month',
        unit: 'count',
        period: 'month',
        blocking: true,
    },
    {
        key: 'messages_per_month',
        column: 'max_messages_per_month',
        label: 'Messages per month',
        unit: 'count',
        period: 'month',
        blocking: true,
    },
    {
        key: 'exports_per_month',
        column: 'max_exports_per_month',
        label: 'Data exports per month',
        unit: 'count',
        period: 'month',
        blocking: true,
    },
    {
        key: 'storage_mb',
        column: 'max_storage_mb',
        label: 'File storage',
        unit: 'mb',
        period: 'total',
        blocking: true,
    },
    {
        key: 'products',
        column: 'max_products',
        label: 'Products',
        unit: 'count',
        period: 'total',
        blocking: true,
    },
    {
        key: 'requests_per_minute',
        column: 'max_requests_per_minute',
        label: 'API requests per minute',
        unit: 'per_minute',
        period: 'minute',
        blocking: false,
    },
] as const satisfies readonly PlanLimitDefinition[];

export type PlanLimitKey = (typeof PLAN_LIMITS)[number]['key'];

/** Counters in `tenant_usage.metric`. */
export type UsageMetric =
    | 'invoices'
    | 'reports'
    | 'messages'
    | 'exports'
    | 'storage_bytes'
    | 'products';

export type PlanSupportLevel = 'EMAIL' | 'PRIORITY' | 'DEDICATED';

/** After overrides: limit = override ?? plan ?? null; features = plan ∪ extra_features. */
export interface PlanEntitlements {
    plan_name: string;
    features: PlanFeatureKey[];
    /** null = unlimited. */
    limits: Record<PlanLimitKey, number | null>;
}

export interface UsageMetricSnapshot {
    /** A counter, or a seat count read live (`users`, `locations`). */
    metric: UsageMetric | 'users' | 'locations';
    used: number;
    /** null = unlimited. Same unit as `used` — bytes for `storage_bytes`. */
    limit: number | null;
    /** null when unlimited. */
    percent: number | null;
}
